import { Flex, Skeleton } from "@chakra-ui/react";
import OutlineButton from "components/Button/OutlineButton";
import PrimaryButton from "components/Button/PrimaryButton";
import WarningPopover from "components/WarningPopover";
import { TipoArrecadacaoEnum } from "features/arrecadacoes/enums";
import { ArrecadacaoItemResponse } from "features/arrecadacoes/typings";
import { showConfirm } from "infra/helpers/alerta";
import history from "infra/helpers/history";
import moment from "moment";
import { useState } from "react";
import { AiOutlineEye, AiOutlinePlayCircle } from "react-icons/ai";
import { BiSpreadsheet } from "react-icons/bi";
import ModalParamsAdicionaisArrecadacao from "ui/components/ModalParamsAdicionaisArrecadacao";
import { useGerarBoleto } from "../../../hooks/useGerarBoleto";
import ModalConfirmarArrecadacaoVencida from "./ModalConfirmarArrecadacaoVencida/index";
import ModalSimulacao from "./ModalSimulacao";

function ButtonGerarBoleto({
  ano,
  mes,
  refetch,
  arrecadacoes,
  selectedArrecadacoes,
}: {
  ano: string;
  mes: string;
  refetch?: () => any;
  arrecadacoes: ArrecadacaoItemResponse[];
  selectedArrecadacoes?: ArrecadacaoItemResponse[];
}) {
  const [
    confirmModalNumeroArrecadacoesVencidas,
    setConfirmModalNumeroArrecadacoesVencidas,
  ] = useState<number | undefined>();
  const [isOpenModalSimulacao, setIsOpenModalSimulacao] = useState(false);
  const [modalParamsAdicionaisOpen, setModalParamsAdicionaisOpen] =
    useState(false);
  const { handleClick: handleGerarBoleto, isLoading } = useGerarBoleto({
    mes,
    ano,
    selectedArrecadacoesIds: selectedArrecadacoes?.map(
      (arrecadacao) => arrecadacao.id
    ),
  });

  if (isLoading) {
    return <Skeleton w="145px" h="40px" rounded="lg" />;
  }

  const handleNavigate = () => {
    const data = moment(`${ano}-${mes}-01`);

    return history.push(
      `/financeiro/receita/receber?ano=${data.format("YYYY")}&mes=${data.format(
        "MM"
      )}`
    );
  };

  const onGerar = async () => {
    const _arrecadacoes = selectedArrecadacoes?.length ? arrecadacoes.filter(a => selectedArrecadacoes.find(s => s.id === a.id)) : arrecadacoes;

    const arrecadacoesVencidas = _arrecadacoes.filter((arrecadacao) =>
      moment(`${ano}-${mes}-${arrecadacao.diaVencimento}`)
        .startOf("day")
        .isBefore(moment(), "day")
    );

    const arrecadacoesProcessadas = _arrecadacoes.filter(
      (arrecadacao) => arrecadacao.processada
    );

    if (arrecadacoesProcessadas.length) {
      const { isConfirmed } = await showConfirm("Uma ou mais arrecadações selecionadas já foram processadas esse mês. Deseja continuar?");
      if (!isConfirmed) {
        return;
      }
    }

    if (arrecadacoesVencidas.length) {
      setConfirmModalNumeroArrecadacoesVencidas(arrecadacoesVencidas.length);
    } else {
      setModalParamsAdicionaisOpen(true);
    }
  };

  const warningTitle = "Selecione pelo menos uma arrecadação";

  const disableActions = !selectedArrecadacoes?.length;
  return (
    <Flex gap="8px">
      <OutlineButton onClick={() => handleNavigate()}>
        <AiOutlineEye />
        Ver Cobranças
      </OutlineButton>

      {mes && ano ? (
        <WarningPopover
          title={warningTitle}
          active={!selectedArrecadacoes?.length}
        >
          <OutlineButton
            onClick={() =>
              disableActions ? null : setIsOpenModalSimulacao(true)
            }
            cursor={disableActions ? "not-allowed" : undefined}
            opacity={disableActions ? 0.6 : 1}
          >
            <BiSpreadsheet />
            Simular
          </OutlineButton>
        </WarningPopover>
      ) : null}

      <WarningPopover
        title={warningTitle}
        active={!selectedArrecadacoes?.length}
      >
        <PrimaryButton
          isLoading={isLoading}
          isDisabled={isLoading}
          cursor={disableActions ? "not-allowed" : undefined}
          opacity={disableActions ? 0.6 : 1}
          onClick={disableActions ? undefined : onGerar}
        >
          <AiOutlinePlayCircle />
          Gerar Boleto
        </PrimaryButton>
      </WarningPopover>

      {isOpenModalSimulacao && !!mes && !!ano ? (
        <ModalSimulacao
          isOpen={isOpenModalSimulacao}
          onClose={() => setIsOpenModalSimulacao(false)}
          mes={mes}
          ano={ano}
          selectedArrecadacoes={selectedArrecadacoes}
        />
      ) : null}

      {confirmModalNumeroArrecadacoesVencidas ? (
        <ModalConfirmarArrecadacaoVencida
          isOpen={!!confirmModalNumeroArrecadacoesVencidas}
          onClose={() => setConfirmModalNumeroArrecadacoesVencidas(undefined)}
          numeroArrecadacoesSelecionadas={
            selectedArrecadacoes?.length ?? arrecadacoes.length
          }
          numeroArrecadacoesVencidas={confirmModalNumeroArrecadacoesVencidas}
          onSubmit={() => {
            setModalParamsAdicionaisOpen(true);
            setConfirmModalNumeroArrecadacoesVencidas(undefined);
          }}
        />
      ) : null}

      {modalParamsAdicionaisOpen ? (
        <ModalParamsAdicionaisArrecadacao
          isOpen={modalParamsAdicionaisOpen}
          onClose={() => setModalParamsAdicionaisOpen(false)}
          mes={`${ano}-${mes}`}
          showMesConsumo={selectedArrecadacoes?.some(
            (arrecadacao) =>
              arrecadacao.tipoArrecadacao.id ===
              TipoArrecadacaoEnum.ConsumoAguaGasEnergia
          )}
          onSubmit={(form) => {
            handleGerarBoleto(form).then(() => {
              setModalParamsAdicionaisOpen(false);
              refetch?.();
            });
          }}
        />
      ) : null}
    </Flex>
  );
}

export default ButtonGerarBoleto;
